/**
 * sl-pbux.8 (audit-fix-round-4): Structural deep-equal for DocPageSnapshot,
 * extracted from `view-config.store.ts` so the recursive value-equality
 * algorithm has dedicated unit-test coverage (R4 B L2 follow-up).
 *
 * Replaces a previous `JSON.stringify` compare that was sensitive to object-
 * key insertion order. `Y.Map` iteration order may diverge from the pure
 * transformer's insertion order without affecting semantic equality, so the
 * bytewise compare produced false `aborted` recoveries.
 *
 * Equality contract:
 *   - top-level scalars (snapshotVersion, contentType, workspaceId, docId,
 *     title, materializedAt, rootNodeId) compared directly via `!==`;
 *   - `nodes` Record compared as a set of (id -> node) entries — `Object.keys`
 *     order is irrelevant, only the key set + values matter;
 *   - inside each node, arrays (including `children`) compared in-order
 *     because list order IS semantic for the renderer;
 *   - inside each node, plain object fields compared by key-set then by
 *     recursive value equality.
 */
import type { DocPageSnapshot } from '@streamlain/view-config';

export function deepEqualSnapshot(
  a: DocPageSnapshot,
  b: DocPageSnapshot
): boolean {
  if (
    a.snapshotVersion !== b.snapshotVersion ||
    a.contentType !== b.contentType ||
    a.workspaceId !== b.workspaceId ||
    a.docId !== b.docId ||
    a.title !== b.title ||
    a.materializedAt !== b.materializedAt ||
    a.rootNodeId !== b.rootNodeId
  ) {
    return false;
  }
  const aKeys = Object.keys(a.nodes);
  const bKeys = Object.keys(b.nodes);
  if (aKeys.length !== bKeys.length) return false;
  for (const id of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b.nodes, id)) return false;
    if (!deepEqualValue(a.nodes[id], b.nodes[id])) return false;
  }
  return true;
}

export function deepEqualValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqualValue(a[i], b[i])) return false;
    }
    return true;
  }
  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;
  const aKeys = Object.keys(aObj);
  const bKeys = Object.keys(bObj);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(bObj, k)) return false;
    if (!deepEqualValue(aObj[k], bObj[k])) return false;
  }
  return true;
}
